/**
 * HardDeleteDialog — Wave 6.3 SuperAdmin two-step hard-delete.
 *
 * Required reason code (closed list from /v3/config/purge-reason-codes) +
 * free-text justification (≥ 10 chars). Two-step confirmation: a "Review"
 * step that shows what's about to be deleted (entity snapshot summary) and
 * an "I'm sure" step that fires the purge.
 *
 * Bypass key: same SESSION_BYPASS_KEY pattern as the Wave 6.1 admin editors.
 */

import { useEffect, useState } from 'react';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import { ApiError } from '@retail-ops/api-client';
import type {
  PurgeReasonCode,
  RecordsBrowseRow,
  RecordsHardDeleteResponse,
} from '@retail-ops/shared-types';
import { promptBypassKey } from '@/modules/Admin/editors/_shared/CrudTable';
import { useRecordsApi } from '../hooks/useRecordsApi';
import { TestModeBadge } from '@/modules/_shared/TestModeBadge';

export interface HardDeleteDialogProps {
  open: boolean;
  rows: ReadonlyArray<RecordsBrowseRow>;
  onClose: () => void;
  onCompleted: () => void;
}

const MIN_JUSTIFICATION = 10;

export function HardDeleteDialog({ open, rows, onClose, onCompleted }: HardDeleteDialogProps): JSX.Element {
  const { client, makeSignal } = useRecordsApi();
  const [step, setStep] = useState<'review' | 'confirm'>('review');
  const [reasonCode, setReasonCode] = useState<string>('');
  const [reasonText, setReasonText] = useState<string>('');
  const [reasons, setReasons] = useState<ReadonlyArray<PurgeReasonCode>>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('review');
    setReasonCode('');
    setReasonText('');
    setError(null);
    let cancelled = false;
    client.retail.config.purgeReasonCodes({ signal: makeSignal() }).then(
      (res) => {
        if (cancelled) return;
        const anyRes = res as unknown as { success?: boolean; data?: PurgeReasonCode[] };
        if (anyRes.success !== false) setReasons(anyRes.data ?? []);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const justificationValid = reasonText.trim().length >= MIN_JUSTIFICATION;
  const reasonValid = reasonCode.length > 0;
  const canConfirm = step === 'confirm' && justificationValid && reasonValid && !submitting;

  async function handleConfirm(): Promise<void> {
    if (!canConfirm) return;
    const bypass = promptBypassKey();
    if (!bypass) {
      setError('A bypass key is required to hard-delete.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let res: RecordsHardDeleteResponse;
      if (rows.length === 1 && rows[0]) {
        res = await client.retail.superAdmin.records.purge(
          bypass,
          {
            entityType: rows[0].entityType,
            entityKey: rows[0].entityKey,
            reasonCode,
            reason: reasonText.trim(),
          },
          { signal: makeSignal() },
        );
      } else {
        res = await client.retail.superAdmin.records.purgeBulk(
          bypass,
          {
            records: rows.map((r) => ({ entityType: r.entityType, entityKey: r.entityKey })),
            reasonCode,
            reason: reasonText.trim(),
          },
          { signal: makeSignal() },
        );
      }
      if (!res.success) {
        setError(res.error ?? 'Hard-delete returned success=false.');
        return;
      }
      onCompleted();
    } catch (e: unknown) {
      if (ApiError.isApiError(e)) setError(e.message);
      else if (e instanceof Error) setError(e.message);
      else setError('Unexpected error during hard-delete.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      title={`Hard-delete ${rows.length} record${rows.length === 1 ? '' : 's'}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {step === 'review' && (
            <Button
              variant="danger"
              onClick={() => setStep('confirm')}
              disabled={!reasonValid || !justificationValid}
            >
              Continue
            </Button>
          )}
          {step === 'confirm' && (
            <>
              <Button variant="ghost" onClick={() => setStep('review')} disabled={submitting}>
                Back
              </Button>
              <Button variant="danger" onClick={() => void handleConfirm()} disabled={!canConfirm}>
                {submitting ? 'Deleting…' : 'Hard-delete'}
              </Button>
            </>
          )}
        </>
      }
    >
      <Banner tone="warn" title="This is a permanent SQL delete">
        A JSON snapshot of every record will be written to{' '}
        <code>ao_PurgeRecords</code> before the row is removed. You can restore from the
        Recovery tab if you change your mind.
      </Banner>

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <h3 style={{ margin: '0 0 var(--sp-3)', fontSize: 14 }}>Records to delete</h3>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--ink-800)' }}>
          {rows.slice(0, 8).map((r) => (
            <li key={`${r.entityType}::${r.entityKey}`} style={{ marginBottom: 6 }}>
              <Pill variant="muted">{r.entityType}</Pill>{' '}
              <span style={{ marginLeft: 6 }}>{r.displayLabel}</span>
              {r.customerName && <span style={{ color: 'var(--ink-600)' }}> — {r.customerName}</span>}
              {r.isTestMode && <span style={{ marginLeft: 6 }}><TestModeBadge active size="sm" /></span>}
            </li>
          ))}
          {rows.length > 8 && (
            <li style={{ color: 'var(--ink-600)', fontStyle: 'italic' }}>
              …and {rows.length - 8} more
            </li>
          )}
        </ul>
      </div>

      <div style={{ marginTop: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--ink-500)' }}>
          Reason code <span style={{ color: 'var(--pcb-red)' }}>*</span>
        </label>
        <select
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          style={{
            padding: 'var(--sp-3) var(--sp-4)',
            border: '1px solid var(--ink-300)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
          }}
        >
          <option value="">Select a reason...</option>
          {reasons
            .filter((r) => r.isActive)
            .map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
        </select>
        {(() => {
          const found = reasons.find((r) => r.code === reasonCode);
          if (!found?.helperText) return null;
          return <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{found.helperText}</div>;
        })()}
      </div>

      <div style={{ marginTop: 'var(--sp-4)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--ink-500)' }}>
          Justification <span style={{ color: 'var(--pcb-red)' }}>*</span>
        </label>
        <textarea
          rows={3}
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
          placeholder={`At least ${MIN_JUSTIFICATION} characters — explain why these records must be permanently deleted.`}
          style={{
            padding: 'var(--sp-4)',
            border: '1px solid var(--ink-300)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
            fontFamily: 'var(--font-sans)',
            resize: 'vertical',
          }}
        />
        <div
          style={{
            fontSize: 11,
            color: justificationValid ? 'var(--ink-500)' : 'var(--pcb-red)',
          }}
        >
          {justificationValid
            ? `${reasonText.trim().length} characters`
            : `At least ${MIN_JUSTIFICATION} characters required (${reasonText.trim().length} so far).`}
        </div>
      </div>

      {step === 'confirm' && (
        <Banner tone="err" title="Final confirmation">
          You are about to permanently delete <strong>{rows.length}</strong> record
          {rows.length === 1 ? '' : 's'} from SQL. Click <strong>Hard-delete</strong> to commit.
        </Banner>
      )}

      {error && (
        <Banner tone="err" title="Hard-delete failed">
          {error}
        </Banner>
      )}
    </ModalShell>
  );
}
